/**
 * Billing for the first renewal of a migrated (imported) policy — issue #35280.
 *
 * Background: a renewal's first installment is normally collected on the billing day of month
 * immediately BEFORE the term starts (@ourbranch/billing getNextChargeDate, renewal branch). For a
 * policy Branch imported from a prior carrier that is wrong for exactly one term: the prior carrier
 * kept billing the imported term through its end, so a Branch charge before the renewal's effective
 * date double-bills the member for the same month. From the second renewal on the lineage is
 * Branch-billed end to end and the normal cadence is correct again.
 *
 * The same applies to a pay-in-full renewal, which is otherwise charged 5–15 days before the term
 * starts (base-renewal-creator-manager #generateBillingDayOfMonth): that too falls inside the prior
 * carrier's coverage. Escrow/mortgagee-billed renewals are the one plan left alone — the mortgagee,
 * not the member, is invoiced.
 *
 * Mechanism: rather than a new billing option, the renewal creator stamps the existing Place-on-Hold
 * fields (#33568) on that one renewal — a system-placed `billingHoldUntil` equal to the effective
 * date. Every consumer already honors the hold: @ourbranch/billing moves the first charge date onto
 * it, the billing crons' BigQuery selection picks the policy up on the hold date (and not before),
 * the direct-bill invoice due date follows the charge date, staff-fe shows the reason in the
 * billing-hold banner, and the renewal creator deletes the hold on the NEXT renewal. For an
 * installment plan the billing day of month is also moved onto the effective day so the remaining
 * installments continue monthly from the first one instead of landing twice in the first month; a
 * pay-in-full renewal has no cadence to keep aligned, so its billing day is left as generated.
 *
 * Shared here (audience: universal) so tasks-be's renewal creator and the one-off backfill script
 * (cron-be/policy-fix-scripts/defer-imported-renewal-first-installment) make the identical decision.
 */
import { getDate, parseISO } from 'date-fns';
import { paymentType as paymentTypeLookup } from '@ourbranch/lookups';

/**
 * `policy.source` values a converter writes on the term it imports. The renewal creator sheds the
 * marker on renewal, so it identifies exactly the original imported term. Keep in sync with
 * staff-fe's IMPORTED_POLICY_SOURCES (core/helpers/policy-status). A new converter must keep the
 * `<legacy system>-conversion` shape: the carrier dbt repos (forreston-dbt `is_guidewire_converted`,
 * feeding `automation_renewals.is_imported_term`) test `source` for that suffix rather than a list.
 */
export const IMPORTED_POLICY_SOURCES = ['imt-conversion', 'guidewire-conversion'];

/** `billingHoldPlacedBy` for a hold the platform placed itself (staff holds carry a username). */
export const SYSTEM_BILLING_HOLD_PLACED_BY = 'system';

/** Shown to staff in the billing-hold banner; doubles as the "this policy was migrated" signal. */
export const MIGRATED_RENEWAL_FIRST_INSTALLMENT_HOLD_REASON =
  'Migrated policy: first installment deferred to the term effective date (the prior carrier billed the imported term)';

// Both converters cap the derived billing day here to avoid month-end drift; staff-fe offers 1-28.
const MAX_BILLING_DAY_OF_MONTH = 28;

export interface ImportedTermFields {
  source?: string | null;
  wasRenewedFromImportedTerm?: boolean | null;
}

export interface MigratedRenewalFields {
  effectiveDate?: string | null;
  paymentType?: string | null;
}

export interface MigratedRenewalFirstInstallmentDeferral {
  billingHoldUntil: string;
  billingHoldReason: string;
  billingHoldPlacedBy: string;
  billingHoldPlacedAt: string;
  /** Installment plans only — moved onto the effective day. Absent for pay-in-full. */
  billingDayOfMonth?: number;
}

/**
 * True for the term a converter wrote — the one the prior carrier billed. Renewals shed `source`
 * and carry `wasRenewedFromImportedTerm` (#33009); a renewal that somehow kept its source marker
 * is still a renewal, so the flag wins.
 */
export function isOriginalImportedTerm(policy: ImportedTermFields | null | undefined): boolean {
  if (!policy || policy.wasRenewedFromImportedTerm === true) {
    return false;
  }
  return IMPORTED_POLICY_SOURCES.includes(policy.source ?? '');
}

/** Monthly / 4 payments / 2 payments — the plans with a per-installment billing day. */
export function isInstallmentPaymentType(paymentType: string | null | undefined): boolean {
  return (
    !!paymentType &&
    [paymentTypeLookup.Monthly, paymentTypeLookup.QuarterTerm, paymentTypeLookup.HalfTerm].includes(paymentType)
  );
}

/** Pay-in-full or an installment plan — every plan where the member (not a mortgagee) is billed. */
export function isMemberBilledPaymentType(paymentType: string | null | undefined): boolean {
  return paymentType === paymentTypeLookup.OneTime || isInstallmentPaymentType(paymentType);
}

/**
 * The Place-on-Hold fields (plus, for installment plans, the billing day) to stamp on `renewal`, or
 * null when the deferral does not apply: `expiringTerm` is not the original imported term, the
 * renewal is escrow-billed (or has no payment type), or it has no effective date to hold to. Pure,
 * so the renewal creator and the backfill agree.
 */
export function getMigratedRenewalFirstInstallmentDeferral({
  expiringTerm,
  renewal,
  nowIso,
  importedTermsBilledByBranch = false
}: {
  expiringTerm: ImportedTermFields | null | undefined;
  renewal: MigratedRenewalFields;
  nowIso: string;
  /**
   * The brand bills its imported terms itself (CFM, #36230 — Brand.areImportedPoliciesBillable): the
   * imported term's installments run to its end in Branch, so the renewal's normal cadence is right
   * and nothing is deferred. This deferral is for the migrate-at-renewal model (Forreston).
   */
  importedTermsBilledByBranch?: boolean;
}): MigratedRenewalFirstInstallmentDeferral | null {
  if (
    importedTermsBilledByBranch ||
    !isOriginalImportedTerm(expiringTerm) ||
    !isMemberBilledPaymentType(renewal.paymentType)
  ) {
    return null;
  }
  if (!renewal.effectiveDate) {
    return null;
  }
  const hold = {
    billingHoldUntil: renewal.effectiveDate,
    billingHoldReason: MIGRATED_RENEWAL_FIRST_INSTALLMENT_HOLD_REASON,
    billingHoldPlacedBy: SYSTEM_BILLING_HOLD_PLACED_BY,
    billingHoldPlacedAt: nowIso
  };
  if (!isInstallmentPaymentType(renewal.paymentType)) {
    return hold;
  }
  return {
    ...hold,
    billingDayOfMonth: Math.min(getDate(parseISO(renewal.effectiveDate)), MAX_BILLING_DAY_OF_MONTH)
  };
}
